import { createMemo, Component, Show } from 'solid-js';
import { capitalize, date, getWeekDays, getCalendarDays } from '../Utils';

export interface CalendarDate {
    year: number;
    month: number;
}

interface CalendarProps {
    year: number;
    month: number;
    activeDates: string[];
    selectedDate: number|null;
    onSelect(day: number): void;
    onNext(date: CalendarDate): void;
    onPrev(date: CalendarDate): void;
}

const HeaderFormatter = Intl.DateTimeFormat('sv-SE', {
    month: 'long',
    year: 'numeric',
});

const Calendar: Component<CalendarProps> = (props) => {
    const weekDays = getWeekDays('sv-SE').map(x => capitalize(x.substring(0, 3)));

    const header = createMemo(() => {
        return capitalize(HeaderFormatter.format(date(props.year, props.month, 1)));
    });

    const calendarDays = createMemo(() => {
        return getCalendarDays(props.year, props.month);
    });

    const hasPrev = createMemo(() => {
        const prevDate = date(props.year, props.month - 1, 1);

        const nowDate = new Date();
        const now = date(nowDate.getFullYear(), nowDate.getMonth() + 1, 1);

        return prevDate >= now;
    });

    function onNext() {
        const next = date(props.year, props.month + 1, 1);
        const res: CalendarDate = {
            year: next.getFullYear(),
            month: 1 + next.getMonth(),
        };

        props?.onNext?.(res);
    }

    function onPrev() {
        // Prevent Marty from creating a time paradox by going back in time
        if (!hasPrev()) return;

        const next = date(props.year, props.month - 1, 1);
        const res: CalendarDate = {
            year: next.getFullYear(),
            month: 1 + next.getMonth(),
        };

        props?.onPrev?.(res);
    }

    function renderDay(dateString: string) {
        const dt = new Date(dateString);
        // If not in the current month, view the day as inactive
        const isTransitive = dt.getFullYear() != props.year || dt.getMonth() != (props.month - 1);
        const isActive = props.activeDates.includes(dateString);
        const isSelected = props.selectedDate == dt.getDate();

        const classList = {
            'citybreak__calendar-day--is-selected': !isTransitive && isSelected,
            'citybreak__calendar-day--is-active': !isTransitive && isActive,
            'citybreak__calendar-day--is-transitive': isTransitive,
        };
        
        function onClick() {
            props.onSelect(dt.getDate());
        }

        return (
            <button type="button"
                    aria-label={`Välj ${dt.getDate()} den ${header()}`}
                    title={`Välj ${dt.getDate()} den ${header()}`}
                    disabled={!isActive}
                    onClick={onClick}
                    classList={classList}
                    class="citybreak__calendar-day">
                <Show when={!isTransitive}>
                    {dt.getDate()}
                </Show>
            </button>
        );
    }

    return (
        <div class="citybreak__calendar">
            <div class="citybreak__calendar-controls">
                {/* Datepicker controls */}
                <button type="button"
                        disabled={!hasPrev()}
                        aria-label="Föregående månad"
                        title="Föregående månad"
                        onClick={onPrev}
                        class="citybreak__calendar-control">
                    <svg stroke="currentColor" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M19 12H5m7 7-7-7 7-7"/>
                    </svg>
                </button>
                <span class="citybreak__calendar-header">
                  {header}
                </span>
                <button type="button"
                        aria-label="Nästa månad"
                        title="Nästa månad"
                        onClick={onNext}
                        class="citybreak__calendar-control">
                    <svg stroke="currentColor" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-7-7 7 7-7 7"/>
                    </svg>
                </button>
            </div>
            <div class="citybreak__calendar-body">
                {/* Datepicker Day-of-week Header */}
                <div class="citybreak__calendar-weekdays">
                    {weekDays.map(weekDay => (
                        <span class="citybreak__calendar-weekday">
                          {weekDay}
                        </span>
                    ))}
                </div>
                {/* Datepicker Days */}
                <div class="citybreak__calendar-days">
                    {calendarDays().map(renderDay)}
                </div>
            </div>
        </div>
    );
}

export default Calendar;
